"use client";

/**
 * Carousel Renderer
 * Renders carousel component in the canvas with interactive navigation
 * In builder mode: clickable images open the edit modal in the sidebar
 */

import type React from "react";
import type { CarouselComponent } from "../../../_types";
import { useCanvasImageClick } from "../../../_hooks/useCanvasImageClick";
import { useCanvasImageUpload } from "../../../_hooks/useCanvasImageUpload";
import { usePublicViewContext } from "../../public/PublicViewContext";
import { ImageCarousel } from "../../public/ImageCarousel";
import { ImageCarouselPreview } from "./_shared/ImageCarouselPreview";
import { InlineTipTapEdit } from "./_shared/InlineTipTapEdit";
import { SectionContainer } from "./_shared/SectionContainer";
import { SECTION_PROSE_CLASSES } from "./_shared/prose-section-text";

interface CarouselRendererProps {
  component: CarouselComponent;
}

/**
 * Renders a carousel section with interactive navigation in builder mode
 */
export function CarouselRenderer({ component }: CarouselRendererProps): React.ReactElement {
  const { title, description, images } = component;
  const { isPublic } = usePublicViewContext();
  const { handleImageClick } = useCanvasImageClick(component.id, "carousel");
  const handleFilesSelected = useCanvasImageUpload(component.id, images);

  return (
    <SectionContainer
      maxWidth="4xl"
      background={component.background}
      title={title || ""}
      componentId={component.id}
      titleFieldId="carousel-title"
    >
      <InlineTipTapEdit
        content={description || ""}
        className={`prose-base ${SECTION_PROSE_CLASSES}`}
        componentId={component.id}
        fieldId="carousel-description"
        field="description"
        placeholder="Click to add description..."
      />
      {isPublic ? (
        images && images.length > 0 ? (
          <div className="relative">
            <ImageCarousel images={images} />
          </div>
        ) : null
      ) : (
        <div className="relative">
          <ImageCarouselPreview images={images} onImageClick={handleImageClick} onFilesSelected={handleFilesSelected} />
        </div>
      )}
    </SectionContainer>
  );
}
